(function () {
  const SLOT_SELECTOR = '#giftie-greeting-card-button-slot';
  const CARD_LINE_PROPERTY = '_giftie_greeting_card_session_id';
  const TARGET_PRODUCT_PROPERTY = '_giftie_greeting_card_target_product_id';
  const FLOW_TIMEOUT_MS = 10000;
  const FRONT_PAGE_INDEX = 1;
  const DEFAULT_INSIDE_PAGE_INDEX = 2;
  const PORTRAIT_FOLDED_INSIDE_PAGE_INDEX = 3;
  const INSIDE_MESSAGE_ROLE = 'inside-message';

  let pendingFlow = null;
  let selectedDesign = null;
  let suppressCancelSource = null;
  let suppressCancelUntil = 0;

  function getErrorMessage(error) {
    return error instanceof Error && error.message
      ? error.message
      : 'Unable to save your greeting card. Please try again.';
  }

  function showFlowError(flow, error) {
    const message = getErrorMessage(error);
    if (flow && typeof flow.onError === 'function') {
      flow.onError(message);
      return;
    }
    window.alert(message);
  }

  function waitFor(getValue, timeoutMs = FLOW_TIMEOUT_MS) {
    const startedAt = Date.now();

    return new Promise((resolve) => {
      const check = () => {
        const value = getValue();
        if (value) {
          resolve(value);
          return;
        }
        if (Date.now() - startedAt >= timeoutMs) {
          resolve(null);
          return;
        }
        window.setTimeout(check, 100);
      };

      check();
    });
  }

  function findProductTrigger() {
    const triggers = Array.from(document.querySelectorAll(`${SLOT_SELECTOR} [data-role="trigger"]`));
    return triggers.find((trigger) => {
      const entry = trigger.closest('[id^="giftie-greeting-card-entry-"]');
      return entry && entry.id !== 'giftie-greeting-card-entry-giftie-combined';
    }) || triggers[0] || null;
  }

  function resetSavedCardBeforeProductFlow() {
    const reconcilers = window.__giftieGreetingCardCartReconcilers;
    if (!(reconcilers instanceof Set)) return;

    reconcilers.forEach((reconcile) => {
      if (typeof reconcile === 'function') reconcile({ items: [] });
    });
  }

  function findBuilderDialog(sourceWindow) {
    return Array.from(document.querySelectorAll('dialog.giftie-gcb-overlay')).find((dialog) => {
      const iframe = dialog.querySelector('iframe[data-role="iframe"]');
      return iframe && iframe.contentWindow === sourceWindow;
    }) || null;
  }

  function closeBuilder(sourceWindow) {
    const dialog = findBuilderDialog(sourceWindow);
    const closeButton = dialog && dialog.querySelector('[data-role="close-modal"]');
    if (closeButton) closeButton.click();
  }

  function rememberSelectedDesign(button) {
    const image = button.querySelector('img');
    const label = button.getAttribute('aria-label') || '';
    const orientation = image && image.naturalWidth && image.naturalHeight
      ? (image.naturalWidth >= image.naturalHeight ? 'landscape' : 'portrait')
      : '';

    selectedDesign = {
      id: button.dataset.designId || '',
      name: label.replace(/^Select\s+/i, '').trim(),
      orientation,
    };
  }

  function resolveVariantId(payload) {
    const payloadVariantId = Number(payload.shopifyVariantId);
    if (Number.isFinite(payloadVariantId) && payloadVariantId > 0) return payloadVariantId;
    return null;
  }

  function resolveGreetingCardMessage(payload, cardFormat) {
    if (typeof payload?.inside?.text === 'string') {
      return richTextToPlainText(payload.inside.text);
    }

    const insidePage = findInsidePage(payload?.pages, cardFormat);
    if (!insidePage) return '';

    const textBlocks = collectTextBlocks(insidePage.blocks);
    const markedBlocks = textBlocks.filter((block) => {
      return block?.attributes?.giftieRole === INSIDE_MESSAGE_ROLE;
    });

    if (markedBlocks.length === 1) {
      return richTextToPlainText(readBlockText(markedBlocks[0]));
    }

    if (markedBlocks.length === 0 && textBlocks.length === 1) {
      return richTextToPlainText(readBlockText(textBlocks[0]));
    }

    return '';
  }

  function findInsidePage(pages, cardFormat) {
    if (!Array.isArray(pages)) return null;

    const frontPage = pages.find((page) => Number(page?.page_index) === FRONT_PAGE_INDEX);
    const orientation = frontPage?.orientation || pages[0]?.orientation || 'portrait';
    const insidePageIndex = cardFormat === 'folded' && orientation !== 'landscape'
      ? PORTRAIT_FOLDED_INSIDE_PAGE_INDEX
      : DEFAULT_INSIDE_PAGE_INDEX;

    return pages.find((page) => Number(page?.page_index) === insidePageIndex) || null;
  }

  function collectTextBlocks(blocks) {
    if (!Array.isArray(blocks)) return [];

    return blocks.flatMap((block) => {
      if (!block || typeof block !== 'object') return [];

      const nestedTextBlocks = collectTextBlocks(block.children);
      return block.type === 'text' ? [block, ...nestedTextBlocks] : nestedTextBlocks;
    });
  }

  function readBlockText(block) {
    if (!block || typeof block !== 'object') return '';

    if (typeof block.text === 'string' || typeof block.text === 'number') {
      return String(block.text);
    }

    if (!Array.isArray(block.children)) return '';

    return block.children.map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      return readBlockText(child);
    }).join('');
  }

  function richTextToPlainText(value) {
    const htmlWithLineBreaks = String(value)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(div|p|li|h[1-6])>/gi, '\n');
    const parsedDocument = new DOMParser().parseFromString(htmlWithLineBreaks, 'text/html');

    return (parsedDocument.body.textContent || '')
      .replace(/\u00A0/g, ' ')
      .replace(/\r\n?/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function buildGreetingCardItem(payload, flow) {
    const fallback = flow && flow.detail ? flow.detail : {};
    const designId = String(
      payload.templateId
      || payload.designId
      || (selectedDesign && selectedDesign.id)
      || fallback.designId
      || ''
    );
    const variantId = resolveVariantId(payload);
    if (!variantId) {
      throw new Error('This greeting-card design is not connected to a Shopify variant.');
    }

    const designName = (selectedDesign && selectedDesign.id === designId && selectedDesign.name)
      || fallback.designName
      || 'Greeting Card';
    const cardFormat = payload.cardFormat
      || document.querySelector(SLOT_SELECTOR)?.dataset.cardFormat
      || 'flat';
    const message = resolveGreetingCardMessage(payload, cardFormat);
    const cartMessagePropertyMode =
      window.__giftieGreetingCardSharedConfig?.cartMessagePropertyMode;

    const properties = {
      _title: designName,
      _giftie_greeting_card_design_id: designId,
      _giftie_greeting_card_format: cardFormat,
      _giftie_greeting_card_orientation:
        (selectedDesign && selectedDesign.id === designId && selectedDesign.orientation)
        || fallback.orientation
        || '',
      _giftie_greeting_card_session_id: String(payload.sessionId || ''),
      'Greeting Card Design': designName,
    };

    if (fallback.mainProductGroup) properties._uniq_id = fallback.mainProductGroup;
    if (fallback.forTitle) properties.For = fallback.forTitle;
    if (fallback.targetProductId) {
      properties[TARGET_PRODUCT_PROPERTY] = String(fallback.targetProductId);
    }
    if (cartMessagePropertyMode === 'visible') {
      properties['Greeting Card Message'] = message;
    } else if (cartMessagePropertyMode === 'hidden') {
      properties['_Greeting Card Message'] = message;
    }

    return {
      id: variantId,
      quantity: 1,
      properties,
    };
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  }

  function findGreetingCardLine(cart, detail) {
    return cart.items.find((item) => item.key === detail.cartLineKey)
      || cart.items.find((item) => {
        const properties = item.properties || {};
        return String(properties[CARD_LINE_PROPERTY] || '') === String(detail.sessionId || '')
          && String(properties._uniq_id || '') === String(detail.mainProductGroup || '');
      });
  }

  async function removeGreetingCardLine(cardLine) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: cardLine.key,
        quantity: 0,
      }),
    });
    if (!response.ok) throw new Error('Unable to replace the existing greeting card.');
    return cardLine;
  }

  function getCartSections() {
    const drawerContent = document.getElementById('cart-drawer__content');
    return [drawerContent && drawerContent.dataset.id, 'cart-icon-bubble'].filter(Boolean);
  }

  function getSectionInnerHtml(html, selector) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const element = documentFragment.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  function renderCartSections(sections) {
    if (!sections) return;

    const drawerContent = document.getElementById('cart-drawer__content');
    const drawerSectionId = drawerContent && drawerContent.dataset.id;
    if (drawerContent && drawerSectionId && sections[drawerSectionId]) {
      drawerContent.innerHTML = getSectionInnerHtml(
        sections[drawerSectionId],
        '.cart-drawer__content'
      );
    }

    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && sections['cart-icon-bubble']) {
      const sectionHtml = getSectionInnerHtml(sections['cart-icon-bubble'], '.shopify-section');
      if (sectionHtml) cartIcon.innerHTML = sectionHtml;
    }
  }

  async function replaceEditedGreetingCard(cardItem, detail) {
    const cart = await fetchCart();
    const previousCardLine = findGreetingCardLine(cart, detail);
    if (!previousCardLine) {
      throw new Error('The greeting card being edited is no longer in the cart.');
    }

    const previousProperties = { ...(previousCardLine.properties || {}) };
    delete previousProperties['Greeting Card Message'];
    delete previousProperties['_Greeting Card Message'];
    delete previousProperties._giftie_greeting_card_thumbnail_url;
    cardItem.properties = { ...previousProperties, ...cardItem.properties };
    if (Object.prototype.hasOwnProperty.call(previousProperties, '_uniq_id')) {
      cardItem.properties._uniq_id = previousProperties._uniq_id;
    }

    const groupId = detail && detail.mainProductGroup;
    const designName = cardItem.properties && cardItem.properties['Greeting Card Design'];
    const mainProduct = groupId && cart.items.find((item) => {
      return String(item.properties?._main_product || '') === String(groupId);
    });

    if (mainProduct && designName && mainProduct.properties?.With !== designName) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: mainProduct.key,
          quantity: mainProduct.quantity,
          properties: {
            ...(mainProduct.properties || {}),
            With: designName,
          },
        }),
      });
      if (!response.ok) throw new Error('Unable to update the gift with the edited greeting card.');
    }

    let response;
    if (String(previousCardLine.variant_id) === String(cardItem.id)) {
      response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: previousCardLine.key,
          quantity: previousCardLine.quantity,
          properties: cardItem.properties,
          sections: getCartSections(),
          sections_url: window.location.pathname,
        }),
      });
    } else {
      await removeGreetingCardLine(previousCardLine);
      response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [cardItem],
          sections: getCartSections(),
          sections_url: window.location.pathname,
        }),
      });
    }
    const cartState = await response.json();
    if (!response.ok || cartState.status) {
      throw new Error(cartState.description || cartState.message || 'Unable to update the greeting card.');
    }

    renderCartSections(cartState.sections);
    if (typeof window.updateCartAttribute === 'function') window.updateCartAttribute();
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();

    document.dispatchEvent(new CustomEvent('cart:update', {
      detail: { source: 'freshsends-giftie', cartData: cartState },
    }));
  }

  async function completeFlow(flow, cardItem) {
    try {
      if (flow.mode === 'edit') {
        await replaceEditedGreetingCard(cardItem, flow.detail);
      } else {
        await flow.onSaved(cardItem);
      }
    } catch (error) {
      showFlowError(flow, error);
    }
  }

  async function startProductFlow(options) {
    if (pendingFlow) return false;

    const flow = {
      mode: 'product',
      form: options.form,
      onSaved: options.onSaved,
      onError: options.onError,
      detail: {
        targetProductId: options.productId
          || options.form?.querySelector('[data-product-id]')?.dataset.productId
          || document.body?.dataset.freshsendsGiftieProductId
          || '',
      },
    };
    pendingFlow = flow;
    selectedDesign = null;

    const trigger = await waitFor(findProductTrigger);
    if (!trigger || pendingFlow !== flow) {
      if (pendingFlow === flow) pendingFlow = null;
      showFlowError(flow, new Error('The greeting-card builder is still loading. Please try again.'));
      return false;
    }

    resetSavedCardBeforeProductFlow();
    trigger.click();
    return true;
  }

  async function startEditFlow(button) {
    if (pendingFlow) return;

    const detail = {
      sessionId: button.dataset.sessionId || '',
      designId: button.dataset.designId || '',
      designName: button.dataset.designName || '',
      orientation: button.dataset.orientation || '',
      cartLineKey: button.dataset.cartLineKey || '',
      mainProductGroup: button.dataset.mainProductGroup || '',
      forTitle: button.dataset.forTitle || '',
      targetProductId: button.dataset.targetProductId || '',
    };
    const flow = { mode: 'edit', detail };
    pendingFlow = flow;
    selectedDesign = null;

    const api = await waitFor(() => {
      const giftieApi = window.GiftieGreetingCard;
      return giftieApi && typeof giftieApi.openFromCartLine === 'function'
        ? giftieApi
        : null;
    });

    if (!api || pendingFlow !== flow || api.openFromCartLine(detail) !== true) {
      if (pendingFlow === flow) pendingFlow = null;
      showFlowError(flow, new Error('The greeting-card editor is still loading. Please try again.'));
    }
  }

  function shouldIntercept(form, submitButton) {
    if (!form || !submitButton) return false;
    if (document.body?.dataset.freshsendsGiftieCardProduct === 'true') return false;
    if (document.body?.dataset.freshsendsGiftieRequired === 'true') return true;
    return submitButton.hasAttribute('data-collectionurl');
  }

  window.addEventListener('message', (event) => {
    const payload = event.data && typeof event.data === 'object' ? event.data : {};

    if (
      payload.type === 'gm/cancel'
      && event.source === suppressCancelSource
      && Date.now() < suppressCancelUntil
    ) {
      event.stopImmediatePropagation();
      suppressCancelSource = null;
      suppressCancelUntil = 0;
      return;
    }

    if (payload.type === 'gm/cancel' && pendingFlow && findBuilderDialog(event.source)) {
      return;
    }

    if (payload.type !== 'gm/cardComplete' || !pendingFlow) return;
    if (!findBuilderDialog(event.source)) return;

    event.stopImmediatePropagation();
    const flow = pendingFlow;
    pendingFlow = null;
    suppressCancelSource = event.source;
    suppressCancelUntil = Date.now() + 3000;

    try {
      const cardItem = buildGreetingCardItem(payload.data || {}, flow);
      closeBuilder(event.source);
      void completeFlow(flow, cardItem);
    } catch (error) {
      closeBuilder(event.source);
      showFlowError(flow, error);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const closesGreetingCardFlow = event.target.closest('[data-role="close-sheet"], [data-role="close-modal"]')
      || event.target.matches('dialog.giftie-gcb-overlay');
    if (closesGreetingCardFlow && pendingFlow) pendingFlow = null;

    const designButton = event.target.closest('.giftie-gcb-card[data-design-id]');
    if (designButton) rememberSelectedDesign(designButton);

    const editButton = event.target.closest('[data-freshsends-giftie-edit]');
    if (!editButton) return;
    event.preventDefault();
    void startEditFlow(editButton);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && pendingFlow) pendingFlow = null;
  }, true);

  document.addEventListener('submit', (event) => {
    if (document.body?.dataset.freshsendsGiftieCardProduct !== 'true') return;
    const form = event.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  window.FreshsendsGiftie = {
    shouldIntercept,
    startProductFlow,
  };
})();
